import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react"
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Folder,
  Info,
  Pencil,
} from "lucide-react"

import { ColumnReorderMobileDialog } from "@/components/ColumnReorderMobileDialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { reorderVisibleColumnPair } from "@/lib/columnReorder"
import type {
  AdGroupRow,
  CampaignRow,
  CampaignTotals,
  LiveAdRow,
} from "@/api/hooks/adsManager"
import {
  useAdgroupAds,
  useCampaignAdgroups,
  usePauseAdgroup,
  useResumeAdgroup,
} from "@/api/hooks/adsManager"
import { DuplicateAdgroupModal } from "./DuplicateAdgroupModal"
import {
  EditAdgroupMetricModal,
  type EditableAdgroupMetric,
} from "./EditAdgroupMetricModal"
import {
  getDashboardRoiRowClass,
  ROI_ROW_STYLES,
} from "@/pages/dashboard/lib/roi"
import type { AdsManagerColumn } from "../lib/columns"
import {
  ACCOUNT_COL_WIDTH_DEFAULT,
  ACCOUNT_COL_WIDTH_MAX,
  ACCOUNT_COL_WIDTH_MIN,
  ADS_MANAGER_ACCOUNT_COL_WIDTH_KEY,
  ADS_MANAGER_CAMPAIGN_COL_WIDTH_KEY,
  CAMPAIGN_COL_WIDTH_DEFAULT,
  CAMPAIGN_COL_WIDTH_MAX,
  CAMPAIGN_COL_WIDTH_MIN,
  loadAdsManagerColumnWidth,
  saveAdsManagerColumnWidth,
} from "../lib/columnWidths"
import { formatCellValue } from "../lib/formatters"
import {
  funnelCvrTextClass,
  funnelCvrTitle,
  getFunnelCvrBand,
} from "../lib/funnelCvr"
import { getCampaignAccountName } from "../lib/accountName"
import { ColumnResizeHandle } from "@/components/ColumnResizeHandle"
import type { MetricFilterBounds, MetricFiltersState } from "../lib/metricFilters"
import { isFilterableMetricColumn } from "../lib/metricFilters"
import { ColumnMetricFilter } from "./ColumnMetricFilter"
import { ColumnStatusFilter } from "./ColumnStatusFilter"
import { StatusBadge } from "./StatusBadge"

/** Fixed Actions column width (two compact icon buttons + padding). */
const ACTIONS_COL_WIDTH = 56

function CampaignRowActions({
  campaignId,
  campaignName,
  advertiserId,
  onDetailClick,
  onKeywordsClick,
}: {
  campaignId: string
  campaignName: string
  advertiserId: string
  onDetailClick: (
    campaignId: string,
    campaignName: string,
    advertiserId: string,
  ) => void
  onKeywordsClick: (campaignId: string, campaignName: string) => void
}) {
  return (
    <div className="inline-flex items-center gap-0.5">
      <button
        type="button"
        title="Campaign detail"
        aria-label="Campaign detail"
        onClick={() => onDetailClick(campaignId, campaignName, advertiserId)}
        className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#555] bg-[#3a3a3a] text-white/85 transition-colors hover:border-[#8b9aff] hover:bg-[#4a4a4a]"
      >
        <Info className="h-3 w-3" aria-hidden />
      </button>
      <button
        type="button"
        title="Keywords"
        aria-label="Keywords"
        onClick={() => onKeywordsClick(campaignId, campaignName)}
        className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#555] bg-[#3a3a3a] text-white/85 transition-colors hover:border-[#8b9aff] hover:bg-[#4a4a4a]"
      >
        <Folder className="h-3 w-3" aria-hidden />
      </button>
    </div>
  )
}

interface CampaignTableProps {
  rows: CampaignRow[]
  totals: CampaignTotals | null
  columns: readonly AdsManagerColumn[]
  columnOrder: string[]
  hiddenColumns: Set<string>
  sortBy: string
  sortOrder: "asc" | "desc"
  onSort: (key: string, columnType: "string" | "number") => void
  onColumnOrderChange: (order: string[]) => void
  onPauseResume: (campaignId: string, currentStatus: string) => void
  /** Campaign id currently pausing/resuming; only that toggle is disabled. */
  pauseResumeBusyId?: string | null
  onKeywordsClick: (campaignId: string, campaignName: string) => void
  onDetailClick: (
    campaignId: string,
    campaignName: string,
    advertiserId: string,
  ) => void
  metricFilters?: MetricFiltersState
  onMetricFilterChange?: (
    columnId: string,
    bounds: MetricFilterBounds | null,
  ) => void
  statusFilter?: string
  onStatusFilterChange?: (value: string) => void
  showUnknownStatus?: boolean
  isLoading: boolean
  showToggle?: boolean
  startDate?: string
  endDate?: string
}

/** Metrics that exist on both campaign rows and adgroup expand rows. */
const ADGROUP_COLUMN_VALUES: Record<
  string,
  (ag: AdGroupRow) => number | null
> = {
  // Non-CBO: budgets/bids live on the adgroup; CBO leaves these 0.
  budget: (ag) => ag.budget ?? null,
  bid: (ag) => ag.bid ?? null,
  cost: (ag) => ag.cost,
  impressions: (ag) => ag.impressions,
  clicks: (ag) => ag.clicks,
  conversions: (ag) => ag.conversions,
  cpc: (ag) => ag.cpc,
  cpm: (ag) => ag.cpm,
  ctr: (ag) => ag.ctr,
  cpa: (ag) => ag.cpa,
}

function isAdgroupOn(status: string | null | undefined): boolean {
  const s = (status || "").toUpperCase()
  return s === "ENABLE" || s === "ACTIVE"
}

function isIframePreview(ad: LiveAdRow): boolean {
  if (ad.preview_kind === "iframe") return true
  const url = ad.preview_url || ""
  if (!url) return false
  // Direct media files use <video>/<img>; TikTok ads_preview links need iframe.
  if (/\.(mp4|webm|mov|m4v|jpg|jpeg|png|gif|webp)(\?|$)/i.test(url)) {
    return false
  }
  return /ads_preview|preview|business\.tiktok|tiktok\.com/i.test(url)
}

function slideUrlsFor(ad: LiveAdRow): string[] {
  const fromSlides = (ad.slide_urls || []).filter(Boolean)
  if (fromSlides.length > 0) return fromSlides
  if (ad.thumbnail_url) return [ad.thumbnail_url]
  if (ad.preview_url && !isIframePreview(ad)) return [ad.preview_url]
  return []
}

function AdCreativeThumb({
  ad,
  onPreview,
}: {
  ad: LiveAdRow
  onPreview: (ad: LiveAdRow) => void
}) {
  const slides = slideUrlsFor(ad)
  const src = slides[0] || ad.thumbnail_url
  const slideCount = Math.max(ad.slide_count || 0, slides.length)
  const isCarousel = ad.media_type === "carousel" || slideCount > 1
  const mediaLabel = isCarousel
    ? `Carousel${slideCount > 1 ? ` (${slideCount})` : ""}`
    : ad.media_type === "video"
      ? "Video"
      : ad.media_type === "image"
        ? "Image"
        : "Creative"
  const canPreview = Boolean(
    ad.preview_url || src || slides.length > 0,
  )

  if (!src) {
    if (!canPreview) {
      return (
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-[#2a2a2a] text-[9px] text-white/40"
          title={mediaLabel}
        >
          —
        </div>
      )
    }
    return (
      <button
        type="button"
        onClick={() => onPreview(ad)}
        title={`Preview ${mediaLabel.toLowerCase()}`}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-[#444] bg-[#1a1a1a] text-[10px] text-white/70 hover:border-[#8b9aff] hover:text-white"
      >
        {ad.media_type === "video" ? "▶" : isCarousel ? "⧉" : "▣"}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => canPreview && onPreview(ad)}
      disabled={!canPreview}
      title={canPreview ? `Preview ${mediaLabel.toLowerCase()}` : mediaLabel}
      className="relative h-8 w-9 shrink-0 hover:opacity-90 disabled:cursor-default"
    >
      {isCarousel && slideCount > 1 ? (
        <>
          <span
            aria-hidden
            className="absolute left-1.5 top-0 h-8 w-8 rounded border border-[#555] bg-[#222]"
          />
          <span
            aria-hidden
            className="absolute left-0.5 top-0 h-8 w-8 rounded border border-[#555] bg-[#1a1a1a]"
          />
        </>
      ) : null}
      <span className="absolute left-0 top-0 h-8 w-8 overflow-hidden rounded border border-[#444] bg-[#111]">
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        {ad.media_type === "video" && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 text-[10px] text-white">
            ▶
          </span>
        )}
        {isCarousel && slideCount > 1 && (
          <span className="pointer-events-none absolute bottom-0 right-0 rounded-tl bg-black/70 px-0.5 text-[8px] leading-3 text-white">
            {slideCount}
          </span>
        )}
      </span>
    </button>
  )
}

function AdCreativePreviewModal({
  ad,
  open,
  onOpenChange,
}: {
  ad: LiveAdRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const slides = ad ? slideUrlsFor(ad) : []
  const [slideIdx, setSlideIdx] = useState(0)
  const previewUrl = ad?.preview_url || null
  const useIframe = ad ? isIframePreview(ad) : false
  const isVideo =
    Boolean(previewUrl) &&
    !useIframe &&
    slides.length <= 1 &&
    (ad?.media_type === "video" ||
      /\.(mp4|webm|mov|m4v)(\?|$)/i.test(previewUrl || ""))
  const isCarousel = slides.length > 1 && !isVideo && !useIframe

  useEffect(() => {
    if (open) setSlideIdx(0)
  }, [open, ad?.ad_id])

  useEffect(() => {
    if (!open || !isCarousel) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        setSlideIdx((i) => (i - 1 + slides.length) % slides.length)
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        setSlideIdx((i) => (i + 1) % slides.length)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, isCarousel, slides.length])

  const goPrev = () =>
    setSlideIdx((i) => (i - 1 + slides.length) % slides.length)
  const goNext = () => setSlideIdx((i) => (i + 1) % slides.length)
  const currentSlide = slides[slideIdx] || slides[0] || null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-h-[90dvh] overflow-hidden border-[#4a4a4a] bg-[#1a1a1a] p-0 text-white ${
          useIframe ? "max-w-3xl sm:max-w-3xl" : "max-w-md sm:max-w-lg"
        }`}
      >
        <DialogHeader className="border-b border-[#333] px-4 py-3 pr-12">
          <DialogTitle className="truncate text-sm font-medium text-white">
            {ad?.ad_name || "Creative preview"}
            {isCarousel ? (
              <span className="ml-2 font-normal text-white/45">
                {slideIdx + 1}/{slides.length}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>
        <div className="relative flex max-h-[75dvh] items-center justify-center bg-black p-3">
          {useIframe && previewUrl ? (
            <iframe
              key={previewUrl}
              src={previewUrl}
              title={ad?.ad_name || "Creative preview"}
              className="h-[70dvh] w-full rounded border-0 bg-black"
              allow="autoplay; encrypted-media; fullscreen"
            />
          ) : isVideo && previewUrl ? (
            <video
              key={previewUrl}
              src={previewUrl}
              poster={slides[0] || undefined}
              controls
              autoPlay
              playsInline
              className="max-h-[70dvh] w-full rounded object-contain"
            />
          ) : currentSlide ? (
            <>
              <img
                key={currentSlide}
                src={currentSlide}
                alt={ad?.ad_name || "Creative"}
                className="max-h-[70dvh] w-full rounded object-contain"
                referrerPolicy="no-referrer"
              />
              {isCarousel ? (
                <>
                  <button
                    type="button"
                    onClick={goPrev}
                    aria-label="Previous slide"
                    className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white hover:bg-black/80"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    aria-label="Next slide"
                    className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white hover:bg-black/80"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                    {slides.map((url, i) => (
                      <button
                        key={`${url}-${i}`}
                        type="button"
                        aria-label={`Go to slide ${i + 1}`}
                        onClick={() => setSlideIdx(i)}
                        className={`h-1.5 w-1.5 rounded-full ${
                          i === slideIdx ? "bg-white" : "bg-white/35"
                        }`}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <p className="py-8 text-sm text-white/50">No preview available</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AdgroupAdsPanel({
  adgroupId,
  colSpan,
  showToggle,
  campaignCellStyle,
  accountCellStyle,
  visibleOrder,
}: {
  adgroupId: string
  colSpan: number
  showToggle: boolean
  campaignCellStyle: CSSProperties
  accountCellStyle: CSSProperties
  visibleOrder: string[]
}) {
  const { data, isLoading, isError, error } = useAdgroupAds(adgroupId, {
    enabled: true,
  })
  const rows = data?.data ?? []
  const [previewAd, setPreviewAd] = useState<LiveAdRow | null>(null)

  if (isLoading || isError || rows.length === 0) {
    return (
      <tr className="border-b border-[#2a2a2a]" style={{ backgroundColor: "#121212" }}>
        <td colSpan={colSpan} className="px-3 py-1.5 pl-12 text-[11px]">
          {isLoading ? (
            <span className="text-white/50">Loading ads from TikTok…</span>
          ) : isError ? (
            <span className="text-red-400">
              {(error as Error)?.message || "Failed to load ads"}
            </span>
          ) : (
            <span className="text-white/45">
              No ads returned from TikTok for this adgroup (paused/deleted
              creatives or none live).
            </span>
          )}
        </td>
      </tr>
    )
  }

  return (
    <>
      {rows.map((ad) => (
        <tr
          key={ad.ad_id || ad.ad_name}
          className="border-b border-[#2a2a2a]"
          style={{ backgroundColor: "#121212" }}
        >
          {showToggle && <td className="px-1 py-1" />}
          <td
            className="sticky left-0 z-10 box-border border-r border-[#1f1f1f] p-0 align-middle"
            style={{ ...campaignCellStyle, backgroundColor: "#121212" }}
          >
            <div className="flex min-w-0 items-center gap-1.5 py-1 pl-10 pr-1">
              <AdCreativeThumb ad={ad} onPreview={setPreviewAd} />
              <div className="min-w-0 flex-1">
                <div
                  className="truncate text-left text-[11px] text-white/80"
                  title={`${ad.ad_name} (${ad.ad_id})`}
                >
                  {ad.ad_name}
                </div>
                <div className="truncate text-[10px] text-white/35">
                  {ad.media_type === "carousel" ||
                  (ad.slide_count && ad.slide_count > 1)
                    ? `carousel · ${ad.slide_count || ad.slide_urls?.length || "?"} slides`
                    : ad.media_type}
                  {ad.creative_count && ad.creative_count > 1
                    ? ` · ${ad.creative_count} creatives`
                    : ""}
                  {ad.is_smart_plus ? " · Smart+" : ""}
                </div>
              </div>
            </div>
          </td>
          <td
            className="sticky z-10 box-border border-r border-[#1f1f1f] px-1 py-1 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.35)]"
            style={{
              width: ACTIONS_COL_WIDTH,
              minWidth: ACTIONS_COL_WIDTH,
              maxWidth: ACTIONS_COL_WIDTH,
              left: Number(campaignCellStyle.width),
              backgroundColor: "#121212",
            }}
          />
          <td
            className="hidden p-0 align-middle md:table-cell"
            style={{ ...accountCellStyle, backgroundColor: "#121212" }}
          />
          {visibleOrder.map((colId) => {
            if (colId === "status") {
              return (
                <td
                  key={colId}
                  className="whitespace-nowrap px-1 py-1 text-center"
                >
                  <StatusBadge status={String(ad.status ?? "")} />
                </td>
              )
            }
            return (
              <td
                key={colId}
                className="whitespace-nowrap px-1 py-1 text-right text-white/20"
              />
            )
          })}
        </tr>
      ))}
      <AdCreativePreviewModal
        ad={previewAd}
        open={Boolean(previewAd)}
        onOpenChange={(next) => {
          if (!next) setPreviewAd(null)
        }}
      />
    </>
  )
}

function AdgroupExpandPanel({
  campaignId,
  startDate,
  endDate,
  colSpan,
  visibleOrder,
  showToggle,
  campaignCellStyle,
  accountCellStyle,
  rowBg,
}: {
  campaignId: string
  startDate: string
  endDate: string
  colSpan: number
  visibleOrder: string[]
  showToggle: boolean
  campaignCellStyle: CSSProperties
  accountCellStyle: CSSProperties
  rowBg: string
}) {
  const { data, isLoading, isError, error } = useCampaignAdgroups(
    campaignId,
    startDate,
    endDate,
    { enabled: Boolean(startDate && endDate) },
  )
  const pauseAdgroup = usePauseAdgroup()
  const resumeAdgroup = useResumeAdgroup()
  const busyId =
    pauseAdgroup.isPending || resumeAdgroup.isPending
      ? (pauseAdgroup.variables ?? resumeAdgroup.variables)
      : null
  const rows = data?.data ?? []
  const [dupOpen, setDupOpen] = useState(false)
  const [dupTarget, setDupTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<{
    id: string
    name: string
    field: EditableAdgroupMetric
    value: number | null
  } | null>(null)
  const [expandedAdgroupIds, setExpandedAdgroupIds] = useState<Set<string>>(
    () => new Set(),
  )

  const toggleAds = useCallback((adgroupId: string) => {
    setExpandedAdgroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(adgroupId)) next.delete(adgroupId)
      else next.add(adgroupId)
      return next
    })
  }, [])

  const handleToggle = useCallback(
    (ag: AdGroupRow) => {
      // Only block re-clicks on the adgroup already in flight.
      if (busyId === ag.adgroup_id) return
      if (isAdgroupOn(ag.status)) {
        pauseAdgroup.mutate(ag.adgroup_id)
      } else {
        resumeAdgroup.mutate(ag.adgroup_id)
      }
    },
    [busyId, pauseAdgroup, resumeAdgroup],
  )

  if (isLoading || isError || rows.length === 0) {
    return (
      <tr className="border-b border-[#4a4a4a]" style={{ backgroundColor: rowBg }}>
        <td colSpan={colSpan} className="px-3 py-2 pl-8 text-[11px]">
          {isLoading ? (
            <span className="text-white/60">Loading adgroups…</span>
          ) : isError ? (
            <span className="text-red-400">
              {(error as Error)?.message || "Failed to load adgroups"}
            </span>
          ) : (
            <span className="text-white/60">
              No adgroups stored for this campaign. Re-sync this date to
              load them (revenue-only rows may never have adgroups).
            </span>
          )}
        </td>
      </tr>
    )
  }

  return (
    <>
      {rows.map((ag) => {
        const on = isAdgroupOn(ag.status)
        const toggling = busyId === ag.adgroup_id
        const adsOpen = expandedAdgroupIds.has(ag.adgroup_id)
        return (
          <Fragment key={ag.adgroup_id}>
            <tr
              className="border-b border-[#333]"
              style={{ backgroundColor: "#1a1a1a" }}
            >
              {showToggle && (
                <td className="px-1 py-1 text-center">
                  <label
                    className={`relative inline-block h-4 w-7 align-middle ${
                      toggling ? "cursor-wait opacity-60" : "cursor-pointer"
                    }`}
                    title={on ? "Pause adgroup" : "Resume adgroup"}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={busyId === ag.adgroup_id}
                      onChange={() => handleToggle(ag)}
                      className="peer sr-only"
                    />
                    <span className="absolute inset-0 rounded-full bg-[#555] transition-colors peer-checked:bg-[#10b981]" />
                    <span className="absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform peer-checked:translate-x-3" />
                  </label>
                </td>
              )}
              <td
                className="sticky left-0 z-10 box-border border-r border-[#1f1f1f] p-0 align-middle"
                style={{ ...campaignCellStyle, backgroundColor: "#1a1a1a" }}
              >
                <div className="flex min-w-0 items-center gap-0.5 py-1 pl-4 pr-1">
                  <button
                    type="button"
                    onClick={() => toggleAds(ag.adgroup_id)}
                    title={adsOpen ? "Hide ads" : "Show ads from TikTok"}
                    aria-expanded={adsOpen}
                    aria-label={
                      adsOpen
                        ? `Hide ads for ${ag.adgroup_name}`
                        : `Show ads for ${ag.adgroup_name}`
                    }
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] text-white/55 hover:bg-[#2a2a2a] hover:text-white"
                  >
                    {adsOpen ? "∧" : "∨"}
                  </button>
                  <div
                    className="min-w-0 flex-1 truncate text-left text-xs text-white/85"
                    title={`${ag.adgroup_name} (${ag.adgroup_id})`}
                  >
                    {ag.adgroup_name}
                  </div>
                  <button
                    type="button"
                    title="Duplicate adset"
                    aria-label={`Duplicate ${ag.adgroup_name}`}
                    onClick={() => {
                      setDupTarget({ id: ag.adgroup_id, name: ag.adgroup_name })
                      setDupOpen(true)
                    }}
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-[#5a5a5a] bg-[#2a2a2a] text-[#c8d0ff] hover:border-[#8b9aff] hover:bg-[#3a3a3a] hover:text-white"
                  >
                    <Copy className="h-3 w-3" strokeWidth={2.25} aria-hidden />
                  </button>
                </div>
              </td>
              <td
                className="sticky z-10 box-border border-r border-[#1f1f1f] px-1 py-1 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.35)]"
                style={{
                  width: ACTIONS_COL_WIDTH,
                  minWidth: ACTIONS_COL_WIDTH,
                  maxWidth: ACTIONS_COL_WIDTH,
                  left: Number(campaignCellStyle.width),
                  backgroundColor: "#1a1a1a",
                }}
              />
              <td
                className="hidden p-0 align-middle md:table-cell"
                style={{ ...accountCellStyle, backgroundColor: "#1a1a1a" }}
              />
              {visibleOrder.map((colId) => {
                if (colId === "status") {
                  return (
                    <td
                      key={colId}
                      className="whitespace-nowrap px-1 py-1 text-center"
                    >
                      <StatusBadge status={String(ag.status ?? "")} />
                    </td>
                  )
                }
                const getter = ADGROUP_COLUMN_VALUES[colId]
                const raw = getter ? getter(ag) : null
                // Budget/bid are adgroup settings; blank when unset (CBO / missing).
                const isSetting = colId === "budget" || colId === "bid"
                const hideZeroSetting =
                  isSetting && (raw == null || Number(raw) === 0)
                if (!getter || hideZeroSetting) {
                  return (
                    <td
                      key={colId}
                      className="whitespace-nowrap px-1 py-1 text-right text-white/30"
                    />
                  )
                }
                if (isSetting) {
                  const field = colId as EditableAdgroupMetric
                  return (
                    <td
                      key={colId}
                      className="whitespace-nowrap px-1 py-1 text-right text-xs text-white/85"
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        {formatCellValue(colId, raw)}
                        <button
                          type="button"
                          title={`Edit ${field}`}
                          aria-label={`Edit ${field} for ${ag.adgroup_name}`}
                          onClick={() => {
                            setEditTarget({
                              id: ag.adgroup_id,
                              name: ag.adgroup_name,
                              field,
                              value: Number(raw),
                            })
                            setEditOpen(true)
                          }}
                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-white/45 hover:bg-[#2a2a2a] hover:text-[#c8d0ff]"
                        >
                          <Pencil className="h-2.5 w-2.5" strokeWidth={2.5} />
                        </button>
                      </span>
                    </td>
                  )
                }
                return (
                  <td
                    key={colId}
                    className="whitespace-nowrap px-1 py-1 text-right text-xs text-white/85"
                  >
                    {formatCellValue(colId, raw)}
                  </td>
                )
              })}
            </tr>
            {adsOpen ? (
              <AdgroupAdsPanel
                adgroupId={ag.adgroup_id}
                colSpan={colSpan}
                showToggle={showToggle}
                campaignCellStyle={campaignCellStyle}
                accountCellStyle={accountCellStyle}
                visibleOrder={visibleOrder}
              />
            ) : null}
          </Fragment>
        )
      })}
      <DuplicateAdgroupModal
        open={dupOpen}
        onOpenChange={(next) => {
          setDupOpen(next)
          if (!next) setDupTarget(null)
        }}
        adgroupId={dupTarget?.id ?? null}
        adgroupName={dupTarget?.name}
      />
      <EditAdgroupMetricModal
        key={
          editTarget
            ? `${editTarget.id}-${editTarget.field}-${editTarget.value ?? ""}`
            : "closed"
        }
        open={editOpen}
        onOpenChange={(next) => {
          setEditOpen(next)
          if (!next) setEditTarget(null)
        }}
        adgroupId={editTarget?.id ?? null}
        adgroupName={editTarget?.name}
        field={editTarget?.field ?? "bid"}
        initialValue={editTarget?.value}
      />
    </>
  )
}

export function CampaignTable({
  rows,
  totals,
  columns,
  columnOrder,
  hiddenColumns,
  sortBy,
  sortOrder,
  onSort,
  onColumnOrderChange,
  onPauseResume,
  pauseResumeBusyId = null,
  onKeywordsClick,
  onDetailClick,
  metricFilters = {},
  onMetricFilterChange,
  statusFilter = "all",
  onStatusFilterChange,
  showUnknownStatus = false,
  isLoading,
  showToggle = false,
  startDate = "",
  endDate = "",
}: CampaignTableProps) {
  const dragIdx = useRef<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const [columnReorderOpen, setColumnReorderOpen] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())

  const toggleExpand = useCallback((campaignId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(campaignId)) next.delete(campaignId)
      else next.add(campaignId)
      return next
    })
  }, [])

  const [campaignColWidth, setCampaignColWidth] = useState(() =>
    loadAdsManagerColumnWidth(
      ADS_MANAGER_CAMPAIGN_COL_WIDTH_KEY,
      CAMPAIGN_COL_WIDTH_DEFAULT,
      CAMPAIGN_COL_WIDTH_MIN,
      CAMPAIGN_COL_WIDTH_MAX,
    ),
  )
  const [accountColWidth, setAccountColWidth] = useState(() =>
    loadAdsManagerColumnWidth(
      ADS_MANAGER_ACCOUNT_COL_WIDTH_KEY,
      ACCOUNT_COL_WIDTH_DEFAULT,
      ACCOUNT_COL_WIDTH_MIN,
      ACCOUNT_COL_WIDTH_MAX,
    ),
  )
  const campaignCellStyle = {
    width: campaignColWidth,
    minWidth: campaignColWidth,
    maxWidth: campaignColWidth,
  } as const
  const actionsCellStyle = {
    width: ACTIONS_COL_WIDTH,
    minWidth: ACTIONS_COL_WIDTH,
    maxWidth: ACTIONS_COL_WIDTH,
    left: campaignColWidth,
  } as const
  const accountCellStyle = {
    width: accountColWidth,
    minWidth: accountColWidth,
    maxWidth: accountColWidth,
  } as const

  // Visible columns in user-specified order
  const visibleOrder = columnOrder.filter((id) => !hiddenColumns.has(id))
  const columnMap = new Map(columns.map((c) => [c.id, c]))

  const handleDragStart = useCallback(
    (idx: number) => {
      dragIdx.current = idx
    },
    [],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent, idx: number) => {
      e.preventDefault()
      setDragOverIdx(idx)
    },
    [],
  )

  const handleDrop = useCallback(
    (idx: number) => {
      const from = dragIdx.current
      if (from === null || from === idx) {
        dragIdx.current = null
        setDragOverIdx(null)
        return
      }
      const newOrder = [...columnOrder]
      // Find positions in the full order
      const fromId = visibleOrder[from]
      const toId = visibleOrder[idx]
      const fromFull = newOrder.indexOf(fromId)
      const toFull = newOrder.indexOf(toId)
      newOrder.splice(fromFull, 1)
      newOrder.splice(toFull, 0, fromId)
      onColumnOrderChange(newOrder)
      dragIdx.current = null
      setDragOverIdx(null)
    },
    [columnOrder, visibleOrder, onColumnOrderChange],
  )

  const sortIndicator = (colId: string) => {
    if (sortBy !== colId) return null
    return sortOrder === "asc" ? " \u25B2" : " \u25BC"
  }

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center text-white">
        Loading campaigns...
      </div>
    )
  }

  const reorderDialogItems = visibleOrder.map((id) => ({
    id,
    label: columnMap.get(id)?.label ?? id,
  }))

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full flex-col">
      <div className="mb-1.5 flex shrink-0 justify-end md:hidden">
        <button
          type="button"
          onClick={() => setColumnReorderOpen(true)}
          className="rounded-md border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-[#454545]"
        >
          Reorder columns
        </button>
      </div>
      <ColumnReorderMobileDialog
        open={columnReorderOpen}
        onOpenChange={setColumnReorderOpen}
        title="Reorder columns"
        items={reorderDialogItems}
        onMoveUp={(visibleIdx) => {
          if (visibleIdx <= 0) return
          onColumnOrderChange(
            reorderVisibleColumnPair(
              columnOrder,
              hiddenColumns,
              visibleIdx,
              visibleIdx - 1,
            ),
          )
        }}
        onMoveDown={(visibleIdx) => {
          if (visibleIdx >= visibleOrder.length - 1) return
          onColumnOrderChange(
            reorderVisibleColumnPair(
              columnOrder,
              hiddenColumns,
              visibleIdx,
              visibleIdx + 1,
            ),
          )
        }}
      />
      {/* min-w-0 keeps the flex parent from expanding with w-max table width so
          horizontal overflow scrolls here instead of being clipped by the page. */}
      <div className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-x-contain rounded-md border border-[#4a4a4a] [-webkit-overflow-scrolling:touch] [scrollbar-gutter:stable]">
        <table className="w-max min-w-full border-collapse text-xs leading-snug">
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-[#4a4a4a] bg-[#2d2d2d]">
            {/* Fixed: toggle column */}
            {showToggle && (
              <th className="whitespace-nowrap px-1 py-2.5 text-left text-[11px] font-semibold text-white">
                On/Off
              </th>
            )}
            {/* Fixed: campaign name (width persisted; drag right edge to resize) */}
            <th
              className="sticky left-0 z-10 relative box-border border-r border-[#2a2a2a] bg-[#2d2d2d] px-1 py-2.5 pr-2 text-left text-xs font-semibold text-white"
              style={campaignCellStyle}
              title="Campaign — drag the right edge to resize (touch supported)"
            >
              <span className="block pr-1">Campaign</span>
              <ColumnResizeHandle
                min={CAMPAIGN_COL_WIDTH_MIN}
                max={CAMPAIGN_COL_WIDTH_MAX}
                startWidth={campaignColWidth}
                onResize={setCampaignColWidth}
                onResizeEnd={(w) =>
                  saveAdsManagerColumnWidth(ADS_MANAGER_CAMPAIGN_COL_WIDTH_KEY, w)
                }
              />
            </th>
            {/* Fixed: actions (pinned beside campaign name) */}
            <th
              className="sticky z-10 box-border border-r border-[#2a2a2a] bg-[#2d2d2d] px-1 py-2.5 text-center text-[11px] font-semibold text-white shadow-[4px_0_8px_-2px_rgba(0,0,0,0.45)]"
              style={actionsCellStyle}
            >
              Action
            </th>
            {/* Account hidden on small screens so metric columns fit when scrolling */}
            <th
              className="relative hidden box-border px-1 py-2.5 pr-2 text-left text-xs font-semibold text-white md:table-cell"
              style={accountCellStyle}
              title="Account — drag the right edge to resize"
            >
              <span className="block truncate pr-1">Account</span>
              <ColumnResizeHandle
                min={ACCOUNT_COL_WIDTH_MIN}
                max={ACCOUNT_COL_WIDTH_MAX}
                startWidth={accountColWidth}
                onResize={setAccountColWidth}
                onResizeEnd={(w) =>
                  saveAdsManagerColumnWidth(ADS_MANAGER_ACCOUNT_COL_WIDTH_KEY, w)
                }
              />
            </th>
            {/* Dynamic columns */}
            {visibleOrder.map((colId, idx) => {
              const col = columnMap.get(colId)
              if (!col) return null
              return (
                <th
                  key={colId}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", colId)
                    e.dataTransfer.effectAllowed = "move"
                    handleDragStart(idx)
                  }}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={() => setDragOverIdx(null)}
                  onClick={() => onSort(colId, col.type)}
                  className={`touch-manipulation cursor-pointer select-none whitespace-nowrap px-1 py-2.5 text-right text-[11px] font-semibold text-white hover:text-white/90 md:touch-auto ${
                    dragOverIdx === idx ? "border-l-2 border-[#8b9aff]" : ""
                  }`}
                  title={col.tooltip || col.label}
                >
                  <span className="inline-flex items-center justify-end gap-0.5">
                    <span>
                      {col.label}
                      {sortIndicator(colId)}
                    </span>
                    {colId === "status" && onStatusFilterChange && (
                      <ColumnStatusFilter
                        value={statusFilter}
                        onChange={onStatusFilterChange}
                        showUnknownStatus={showUnknownStatus}
                      />
                    )}
                    {onMetricFilterChange &&
                      isFilterableMetricColumn(colId, col.type) && (
                        <ColumnMetricFilter
                          columnId={colId}
                          label={col.label}
                          bounds={metricFilters[colId]}
                          onApply={(next) =>
                            onMetricFilterChange(colId, next)
                          }
                        />
                      )}
                  </span>
                </th>
              )
            })}
            </tr>
          </thead>
          <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={
                  visibleOrder.length + 3 + (showToggle ? 1 : 0)
                }
                className="py-8 text-center text-white/80"
              >
                No campaigns found
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const roiClass = getDashboardRoiRowClass(row.roi)
              const bgColor = ROI_ROW_STYLES[roiClass].row
              const expanded = expandedIds.has(row.campaign_id)
              const colSpan =
                visibleOrder.length + 3 + (showToggle ? 1 : 0)
              return (
                <Fragment key={row.campaign_id}>
                <tr
                  className="border-b border-[#4a4a4a] hover:brightness-110"
                  style={{ backgroundColor: bgColor }}
                >
                  {showToggle && (
                    <td className="px-1 py-2.5 text-center">
                      <label
                        className={`relative inline-block h-4 w-7 align-middle ${
                          pauseResumeBusyId === row.campaign_id
                            ? "cursor-wait opacity-60"
                            : "cursor-pointer"
                        }`}
                        title={
                          row.status === "ENABLE" || row.status === "ACTIVE"
                            ? "Pause campaign"
                            : "Resume campaign"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={
                            row.status === "ENABLE" || row.status === "ACTIVE"
                          }
                          disabled={pauseResumeBusyId === row.campaign_id}
                          onChange={() =>
                            onPauseResume(row.campaign_id, row.status)
                          }
                          className="peer sr-only"
                        />
                        <span className="absolute inset-0 rounded-full bg-[#555] transition-colors peer-checked:bg-[#10b981]" />
                        <span className="absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform peer-checked:translate-x-3" />
                      </label>
                    </td>
                  )}
                  <td
                    className="sticky left-0 z-10 box-border border-r border-[#1f1f1f] p-0 align-middle"
                    style={{ ...campaignCellStyle, backgroundColor: bgColor }}
                  >
                    <div className="flex min-w-0 items-center gap-0.5 px-1 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleExpand(row.campaign_id)}
                        className="shrink-0 rounded px-0.5 text-[10px] text-white/70 hover:bg-[#4a4a4a] hover:text-white"
                        title={expanded ? "Hide adgroups" : "Show adgroups"}
                        aria-expanded={expanded}
                      >
                        {expanded ? "∧" : "∨"}
                      </button>
                      <div
                        className="min-w-0 truncate text-left text-xs text-white"
                        title={row.campaign_name}
                      >
                        {row.campaign_name}
                      </div>
                    </div>
                  </td>
                  <td
                    className="sticky z-10 box-border border-r border-[#1f1f1f] px-1 py-2.5 text-center align-middle shadow-[4px_0_8px_-2px_rgba(0,0,0,0.35)]"
                    style={{ ...actionsCellStyle, backgroundColor: bgColor }}
                  >
                    <CampaignRowActions
                      campaignId={row.campaign_id}
                      campaignName={row.campaign_name}
                      advertiserId={String(row.advertiser_id ?? "")}
                      onDetailClick={onDetailClick}
                      onKeywordsClick={onKeywordsClick}
                    />
                  </td>
                  <td
                    className="hidden p-0 align-middle md:table-cell"
                    style={accountCellStyle}
                  >
                    <div
                      className="min-w-0 truncate px-1 py-2.5 text-left text-xs text-white"
                      title={getCampaignAccountName(row)}
                    >
                      {getCampaignAccountName(row)}
                    </div>
                  </td>
                  {visibleOrder.map((colId) => {
                    const col = columnMap.get(colId)
                    if (!col) return null
                    const value = row[colId]
                    if (colId === "status") {
                      return (
                        <td
                          key={colId}
                          className="whitespace-nowrap px-1 py-2.5 text-center"
                        >
                          <StatusBadge status={String(value ?? "")} />
                        </td>
                      )
                    }
                    // Bid is adgroup-only. ABO budgets live on adgroups.
                    if (colId === "bid") {
                      return (
                        <td
                          key={colId}
                          className="whitespace-nowrap px-1 py-2.5 text-right text-xs text-white/50"
                        >
                          -
                        </td>
                      )
                    }
                    if (colId === "budget") {
                      // ABO: budgets live on adgroups. Never show a misleading
                      // $0 campaign rollup when is_abo or campaign budget is empty.
                      const campBudget = Number(value)
                      const isAbo =
                        Boolean(row.is_abo) ||
                        !Number.isFinite(campBudget) ||
                        campBudget <= 0
                      return (
                        <td
                          key={colId}
                          className="whitespace-nowrap px-1 py-2.5 text-right text-xs text-white"
                          title={
                            isAbo
                              ? "Ad group budget (ABO) — expand to see / edit"
                              : undefined
                          }
                        >
                          {isAbo ? "ABO" : formatCellValue(colId, value)}
                        </td>
                      )
                    }
                    const funnelBand = getFunnelCvrBand(colId, value)
                    const funnelClass = funnelCvrTextClass(funnelBand)
                    // Prefer band color over text-white — Tailwind utilities
                    // conflict when both are present.
                    return (
                      <td
                        key={colId}
                        title={funnelCvrTitle(colId, funnelBand)}
                        className={`whitespace-nowrap px-1 py-2.5 text-right text-xs ${
                          funnelClass || "text-white"
                        }`}
                      >
                        {formatCellValue(colId, value)}
                      </td>
                    )
                  })}
                </tr>
                {expanded && startDate && endDate ? (
                  <AdgroupExpandPanel
                    campaignId={row.campaign_id}
                    startDate={startDate}
                    endDate={endDate}
                    colSpan={colSpan}
                    visibleOrder={visibleOrder}
                    showToggle={showToggle}
                    campaignCellStyle={campaignCellStyle}
                    accountCellStyle={accountCellStyle}
                    rowBg={bgColor}
                  />
                ) : null}
                </Fragment>
              )
            })
          )}
          </tbody>
          {totals && rows.length > 0 ? (
            <tfoot className="sticky bottom-0 z-20">
              <tr className="border-t-2 border-[#7488e8] bg-[#303030] font-semibold shadow-[0_-1px_0_rgba(0,0,0,0.35)]">
                {showToggle && <td className="px-1 py-2.5" />}
                <td
                  className="sticky left-0 z-10 box-border border-r border-[#2a2a2a] bg-[#303030] px-1 py-2.5 text-left text-xs font-bold text-white"
                  style={campaignCellStyle}
                >
                  Totals
                </td>
                <td
                  className="sticky z-10 box-border border-r border-[#2a2a2a] bg-[#303030] px-1 py-2.5 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.35)]"
                  style={actionsCellStyle}
                />
                <td
                  className="hidden box-border bg-[#303030] px-1 py-2.5 md:table-cell"
                  style={accountCellStyle}
                />
                {visibleOrder.map((colId) => {
                  const col = columnMap.get(colId)
                  if (!col) return null
                  const value = totals[colId]
                  // Bid/budget are settings, not summable totals (esp. ABO).
                  if (colId === "bid" || colId === "budget") {
                    return (
                      <td
                        key={colId}
                        className="whitespace-nowrap px-1 py-2.5 text-right text-xs font-bold text-white/50"
                      >
                        -
                      </td>
                    )
                  }
                  const funnelBand = getFunnelCvrBand(colId, value)
                  const funnelClass = funnelCvrTextClass(funnelBand)
                  return (
                    <td
                      key={colId}
                      title={funnelCvrTitle(colId, funnelBand)}
                      className={`whitespace-nowrap px-1 py-2.5 text-right text-xs font-bold ${
                        funnelClass || "text-white"
                      }`}
                    >
                      {colId === "status" ? "" : formatCellValue(colId, value)}
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  )
}
